export type RouteCapabilityStatus =
  | 'production'
  | 'production_but_incomplete'
  | 'preview_pilot'
  | 'legacy_deprecated'
  | 'hidden'
  | 'member_gated';

export interface RouteCapabilitySpec {
  status: RouteCapabilityStatus;
  label: string;
  detail?: string;
}

/** Public route capability labels aligned with CP-WP-006 route inventory. */
export function resolveRouteCapability(pathname: string): RouteCapabilitySpec | null {
  const parts = pathname.split('/').filter(Boolean);
  const slugSection = parts.length >= 2 ? parts[1] : parts[0];

  if (pathname === '/law-impact' || pathname.startsWith('/law-impact/')) {
    return {
      status: 'member_gated',
      label: 'Members',
      detail:
        'The Policy Intelligence Dashboard and policy library are available to CoolClimate Network members.',
    };
  }

  if (pathname === '/projects' || pathname.startsWith('/projects/')) {
    return {
      status: 'preview_pilot',
      label: 'Preview',
      detail:
        'Member preview workspace for California climate project and funding pipeline records; features may change.',
    };
  }
  if (pathname === '/portal') {
    return {
      status: 'preview_pilot',
      label: 'Preview',
      detail: 'CoolClimate platform tools and portal features are in preview and may change.',
    };
  }
  if (pathname === '/local-abatement-scatter') {
    return {
      status: 'preview_pilot',
      label: 'Preview',
      detail:
        'Explore modeled abatement costs vs. per-capita emissions reductions across California; preview figure.',
    };
  }

  if (slugSection === 'policy-tool') {
    return {
      status: 'production_but_incomplete',
      label: 'Public with member details',
      detail:
        'Main scenario sliders are public. Expanded detailed controls and sub-measure toggles require CoolClimate Network membership.',
    };
  }
  if (slugSection === 'analysis') {
    return {
      status: 'member_gated',
      label: 'Members',
      detail:
        'Statewide analysis dashboards, publication figures, and benchmarking tools require CoolClimate Network membership.',
    };
  }
  if (slugSection === 'coolclimate-calculator') {
    return {
      status: 'preview_pilot',
      label: 'Preview',
      detail:
        'Location-specific calculator embed for member jurisdictions; the public household calculator is at /coolclimate.',
    };
  }
  if (slugSection === 'community') {
    return {
      status: 'legacy_deprecated',
      label: 'Legacy route',
      detail: 'This page has moved. Use the main location overview instead.',
    };
  }

  const legacyRoots = new Set([
    'overview',
    'ghg',
    'people',
    'buildings',
    'transportation',
    'policy-tool',
    'community',
    'other',
    'admin-settings',
    'coolclimate-calculator',
    'analysis',
  ]);
  if (parts.length === 1 && legacyRoots.has(parts[0])) {
    return {
      status: 'legacy_deprecated',
      label: 'Legacy URL',
      detail: 'This address redirects to the current location page.',
    };
  }

  return null;
}
